import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { useSEO } from "../utils/seo";
import { useJsonLd, buildArticleSchema, buildBreadcrumbSchema } from "../utils/schema";
import { trackCTAClick } from "../utils/analytics";
import { Calendar, User, ArrowLeft, Share2, Clock, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  // Dynamic SEO - updates when post loads
  useSEO({
    title: post?.meta_title || post?.title || "Insights | Onward Land Solutions",
    description: post?.meta_description || post?.excerpt || "Read the latest insights from Onward Land Solutions on land project management, right-of-way, easement acquisition, corridor intelligence, GIS tracking, and survey coordination.",
    canonicalPath: `/insights/${slug}`,
    ogTitle: post?.meta_title || post?.title,
    ogDescription: post?.meta_description || post?.excerpt,
    ogImage: post?.featured_image
  });

  // Article structured data
  useJsonLd('ld-article', post ? buildArticleSchema({
    title: post.title,
    slug: post.slug,
    author: post.author,
    publishDate: post.publish_date,
    modifiedDate: post.updated_at || post.publish_date,
    image: post.featured_image,
    excerpt: post.meta_description || post.excerpt
  }) : null);

  // Breadcrumb structured data
  useJsonLd('ld-breadcrumb-blog', post ? buildBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Insights", url: "/insights" },
    { name: post.title }
  ]) : null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setLoadError(false);
    setPost(null);
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/posts/${slug}`);
        if (active) setPost(response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          if (active) setNotFound(true);
        } else {
          console.error("Error fetching blog post:", error);
          if (active) setLoadError(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    const fetchRelatedPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog/posts?limit=3&exclude=${slug}`);
        if (active) setRelatedPosts(response.data);
      } catch (error) {
        console.error("Error fetching related posts:", error);
      }
    };

    fetchPost();
    fetchRelatedPosts();
    return () => { active = false; };
  }, [slug, retry]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.meta_description,
        url: window.location.href,
      });
    } else {
      // Fallback - copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Article URL copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p role="status" data-testid="article-loading">Loading article...</p>
        </div>
      </div>
    );
  }

  if (loadError) return <section className="max-w-4xl mx-auto px-6 py-24" role="alert" data-testid="article-load-error"><h1 className="text-3xl mb-6">Article temporarily unavailable.</h1><button className="ols-btn-primary" onClick={() => setRetry(n => n + 1)} data-testid="article-retry">Try Again</button></section>;

  if (notFound) {
    return <Navigate to="/insights" replace />;
  }

  return (
    <>
      {/* Article Header */}
      <div className="relative pt-24 pb-12">
        {post.featured_image && (
          <>
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-full object-cover opacity-20"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-900/90 to-indigo-950/80"></div>
            </div>
          </>
        )}
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back to Insights */}
          <Link
            to="/insights"
            className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8 transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Insights
          </Link>

          {/* Article Meta */}
          <div className="flex flex-wrap items-center text-sm text-slate-400 mb-6 gap-x-6 gap-y-3" data-testid="article-byline">
            <div className="flex items-center space-x-1">
              <User className="h-4 w-4" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(post.publish_date)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="h-4 w-4" />
              <span>{estimateReadTime(post.body)} min read</span>
            </div>
          </div>

          {/* Article Title */}
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-testid="article-title">
            {post.title}
          </h1>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="inline-flex items-center bg-slate-800/60 hover:bg-slate-700/60 text-white px-4 py-2 rounded-lg transition-colors mb-8"
            data-testid="share-article"
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share Article
          </button>
        </div>
      </div>

      {/* Article Content */}
      <div className="pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.editorial_note && <aside className="border-l-4 border-[#C8102E] pl-5 py-3 mb-8 text-sm leading-relaxed" data-testid="article-editorial-note">{post.editorial_note}</aside>}
          <article className="prose prose-lg prose-invert max-w-none" data-testid="article-body">
            <div 
              className="text-slate-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body) }}
            />
          </article>

          {/* Post-Article CTA */}
          <div className="mt-12 bg-gradient-to-br from-blue-600/15 to-indigo-600/15 border border-blue-700/30 rounded-xl p-8 text-center" data-testid="blog-post-cta">
            <h3 className="text-2xl font-bold text-white mb-3">Talk to Onward</h3>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              Contact Onward to discuss how integrated land services can support your infrastructure project.
            </p>
            <Link
              to="/contact"
              data-testid="article-project-cta"
              onClick={() => trackCTAClick('blog_post_cta')}
              className="inline-flex items-center bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 px-8 rounded-lg shadow-lg shadow-blue-600/30 transition-colors"
            >
              Talk to Onward
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="py-16 border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-2xl font-bold text-white mb-8">Related Articles</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.slice(0, 3).map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/insights/${relatedPost.slug}`}
                  data-testid={`article-related-${relatedPost.slug}`}
                  className="group bg-slate-800/60 backdrop-blur-lg border border-slate-700/50 rounded-lg overflow-hidden hover:border-slate-600/50 transition-all duration-300"
                >
                  <div className="relative h-32 overflow-hidden">
                    <img
                      src={relatedPost.featured_image}
                      alt={relatedPost.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-white mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors">
                      {relatedPost.title}
                    </h4>
                    <p className="text-sm text-slate-400">
                      {formatDate(relatedPost.publish_date)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default BlogPost;
